import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ApiSession, Identity } from './lib/api.js';
import { ApiError, endSession, getAuthConfig, getIdentity, renewSession } from './lib/api.js';
import { describeFailure, failureOf, type Failure } from './lib/failure.js';
import { navigate, useLocation } from './lib/router.js';
import { AcceptInvitation } from './routes/AcceptInvitation.js';
import { CreateEstablishment } from './routes/CreateEstablishment.js';
import { Landing } from './site/Landing.js';
import { SignIn } from './app/SignIn.js';
import { LoadFailure, useAttempt } from './components/LoadFailure.js';
import { Loading, Skeleton } from './design/index.js';

/**
 * The signed-in half of the product, fetched only when somebody signs in.
 *
 * A visitor reading the marketing page has no use for the calls table or the
 * onboarding wizard; making them download it is the difference between a
 * landing page that appears and one that arrives.
 */
const Dashboard = lazy(async () => await import('./app/Dashboard.js'));

/**
 * The application, and the one decision that shapes it.
 *
 * `/` is the public site and `/app` is the dashboard. They are two products
 * with two audiences, and the split has a consequence worth stating: **the
 * landing page renders on the first paint, before any request**. It does not
 * wait to learn whether somebody is signed in, because a marketing page that
 * shows "Chargement…" for half a second has lost the three seconds it had.
 *
 * Everything under `/app` is the opposite: nothing renders until the server
 * has said who this is, because showing a shell and filling it in is how one
 * salon's name appears above another salon's calls for a moment.
 *
 * **Nothing here is an authorisation control.** Hiding a menu item a role
 * cannot use is good manners; the API re-checks every request, because an
 * attacker does not use our UI. If this file and the server ever disagree
 * about what a MANAGER may do, the server is right.
 */

/**
 * Where the API lives.
 *
 * Empty by design: the Worker serves this bundle and answers `/v1` on the same
 * origin, so every request below is a relative URL and the browser sends no
 * preflight at all. `VITE_API_BASE_URL` remains an override for a deployment
 * that splits the two, and the dev server proxies `/v1` to the local Worker so
 * that `pnpm dev` behaves exactly like production.
 */
const API_BASE_URL: string = (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ?? '';

const LOGIN_URL = `${API_BASE_URL}/v1/auth/login`;
const APP_HOME = '/app';
/**
 * Where "Créer mon compte" leads: the same hosted sign-in, marked as a sign-up
 * so the provider may open on its account-creation screen (AUTH_SIGNUP_PARAMS),
 * and back into the dashboard, which offers to create the establishment.
 */
const SIGNUP_URL = `${LOGIN_URL}?intent=signup&return_to=${encodeURIComponent(APP_HOME)}`;
const ACCEPT_PATH = '/invitations/accept';
const APP_PREFIX = '/app';
/** Where a salon that just signed itself up goes next: choosing what it pays for. */
const CHOOSE_PLAN_PATH = '/app/facturation';

export function App(): React.JSX.Element {
  const { path } = useLocation();
  const [token, setToken] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [identity, setIdentity] = useState<Identity | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  /** Who this is could not be read; shown only while there is nobody yet. */
  const [identityFailure, setIdentityFailure] = useState<Failure | null>(null);
  const [identityAttempt, askIdentityAgain] = useAttempt();
  /** Null until we know: a flash of the wrong screen is worse than a moment. */
  const [hostedLogin, setHostedLogin] = useState<boolean | null>(null);
  /** The deployment's sign-in could not be read at all. */
  const [unreachable, setUnreachable] = useState<Failure | null>(null);
  const [startAttempt, startAgain] = useAttempt();
  /**
   * Whether the first renewal is still on its way.
   *
   * Somebody who is signed in arrives with a session cookie and no token, and
   * while one is swapped for the other, `token === null` read as "signed out":
   * the sign-in screen appeared, then the dashboard replaced it. On a slow
   * connection that is long enough to press « Se connecter ».
   */
  const [resuming, setResuming] = useState(true);
  const [signInError, setSignInError] = useState<string | null>(null);

  /**
   * Asks the server for a fresh access token.
   *
   * Used both at startup and on a timer. It never throws at the caller: a
   * failure to renew means "signed out", which is a screen, not an exception.
   */
  const renew = useCallback(async (): Promise<void> => {
    try {
      const session = await renewSession(API_BASE_URL);
      if (session === null) {
        setToken(null);
        setExpiresAt(null);
        return;
      }
      setToken(session.accessToken);
      setExpiresAt(session.expiresAt);
      setSignInError(null);
    } catch (cause: unknown) {
      // The provider being down is not the same as being signed out, and
      // treating it as one would throw away a session the server has taken
      // care to keep. Say what is happening and come back to it.
      if (cause instanceof ApiError && cause.code === 'auth.provider_unavailable') {
        setSignInError(cause.message);
        window.setTimeout(() => void renew(), 30_000);
        return;
      }
      // Nor is a server that never answered, or a network that dropped: that
      // says nothing about the session either. The token this page holds is
      // kept — renewal starts a minute before it runs out — and asked for
      // again.
      if (!(cause instanceof ApiError)) {
        setSignInError('Le serveur ne répond pas. Nouvel essai dans quelques secondes.');
        window.setTimeout(() => void renew(), 30_000);
        return;
      }
      setToken(null);
      setExpiresAt(null);
    }
  }, []);

  /**
   * What happens on the first paint: read the sign-in outcome, then resume.
   *
   * The error code arrives in the URL because the server can only talk to a
   * browser it is redirecting by putting something in the address bar. It is
   * removed immediately afterwards, so a reload — or a link pasted to a
   * colleague — does not resurrect a failure that is over.
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const failure = params.get('auth_error');
    if (failure !== null) {
      setSignInError(signInMessageFor(failure));
      params.delete('auth_error');
      const query = params.toString();
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${query === '' ? '' : `?${query}`}`,
      );
    }

    let cancelled = false;
    setUnreachable(null);
    setResuming(true);
    getAuthConfig(API_BASE_URL)
      .then(async (config) => {
        if (cancelled) return;
        setHostedLogin(config.hostedLogin);
        if (config.hostedLogin) await renew();
      })
      .catch((cause: unknown) => {
        if (!cancelled) setUnreachable(failureOf(cause));
      })
      .finally(() => {
        if (!cancelled) setResuming(false);
      });

    return () => {
      cancelled = true;
    };
  }, [renew, startAttempt]);

  /**
   * Renews shortly before the token expires.
   *
   * A minute of margin, and never sooner than five seconds, so a clock that
   * disagrees with the server's cannot turn this into a request loop.
   */
  useEffect(() => {
    if (expiresAt === null) return;
    const milliseconds = Math.max(5_000, new Date(expiresAt).getTime() - Date.now() - 60_000);
    const timer = window.setTimeout(() => void renew(), milliseconds);
    return () => {
      window.clearTimeout(timer);
    };
  }, [expiresAt, renew]);

  /*
    Asked again with every renewed token, which is how a role changed or an
    establishment removed reaches an open dashboard. A refresh that fails
    leaves the identity already on screen alone: the server re-checks every
    request anyway, and replacing a working dashboard with an error because a
    background refresh missed would be the wrong way round.
  */
  useEffect(() => {
    if (token === null) return;
    let cancelled = false;
    setIdentityFailure(null);

    getIdentity({ baseUrl: API_BASE_URL, token })
      .then((result) => {
        if (cancelled) return;
        setIdentity(result);
        // The first establishment, until the person chooses. Someone who
        // belongs to one salon should never have to pick it.
        setTenantId((current) => current ?? result.memberships[0]?.tenantId ?? null);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setIdentityFailure(failureOf(cause, messageFor));
      });

    return () => {
      cancelled = true;
    };
  }, [token, identityAttempt]);

  /*
    The session's identity outlives the token in it.

    The access token is renewed every few minutes — the provider's default
    lifetime is five — and the session object used to be rebuilt each time.
    Every screen loads its data in an effect that depends on the session, so
    every renewal reloaded the screen: a list went back to its first page under
    a skeleton, and a form filled from the server (Établissement, Horaires,
    Installation) was filled again, erasing whatever had been typed since.

    A renewed token is the same person in the same establishment. So the object
    changes only with the establishment, and the token is read when a request
    is made — always the newest one.
  */
  const latestToken = useRef(token);
  useLayoutEffect(() => {
    latestToken.current = token;
  }, [token]);
  const session = useMemo<ApiSession | null>(
    () =>
      tenantId === null
        ? null
        : {
            baseUrl: API_BASE_URL,
            get token(): string {
              return latestToken.current ?? '';
            },
            tenantId,
          },
    [tenantId],
  );

  const signOut = (): void => {
    const current = token;
    setToken(null);
    setExpiresAt(null);
    setIdentity(null);
    setTenantId(null);
    endSession(API_BASE_URL, current)
      .then((logoutUrl) => {
        // Only the provider can end the session for every other browser. If it
        // gives us nowhere to go, this one is signed out either way.
        if (logoutUrl !== null) window.location.assign(logoutUrl);
      })
      .catch(() => {
        // The same: this browser has already forgotten the session.
      });
  };

  /*
    Ahead of everything else, and that order is the whole point. Somebody
    opening an invitation is not signed in — they have just been sent a link —
    and showing them the ordinary sign-in screen loses the token in the
    fragment, which is the only copy that exists. This screen keeps it, then
    sends them to sign in and comes back to it.
  */
  if (path === ACCEPT_PATH) {
    if (unreachable !== null) {
      return (
        <Plain>
          <LoadFailure
            title="Impossible d’ouvrir cette invitation"
            failure={unreachable}
            onRetry={startAgain}
          />
        </Plain>
      );
    }
    // This screen has to know whether there is a provider to send somebody to,
    // and whether they are already signed in, so it waits — unlike the landing
    // page below, which waits for nothing.
    if (hostedLogin === null || resuming) return <AppSkeleton />;
    return (
      <AcceptInvitation baseUrl={API_BASE_URL} accessToken={token} hostedLogin={hostedLogin} />
    );
  }

  if (path !== APP_PREFIX && !path.startsWith(`${APP_PREFIX}/`)) {
    // The public site. Rendered without waiting for anything.
    return <Landing signedIn={token !== null} loginUrl={LOGIN_URL} signupUrl={SIGNUP_URL} />;
  }

  if (unreachable !== null) {
    return (
      <Plain>
        <LoadFailure
          title="Impossible d’ouvrir votre espace"
          failure={unreachable}
          onRetry={startAgain}
        />
      </Plain>
    );
  }

  if (hostedLogin === null || resuming) return <AppSkeleton />;

  if (token === null) {
    return (
      <SignIn
        hostedLogin={hostedLogin}
        loginUrl={LOGIN_URL}
        error={signInError}
        onToken={setToken}
      />
    );
  }

  if (identity === null) {
    if (identityFailure === null) return <AppSkeleton />;
    return (
      <Plain>
        <LoadFailure
          title="Impossible d’ouvrir votre espace"
          failure={identityFailure}
          onRetry={askIdentityAgain}
        />
      </Plain>
    );
  }

  if (tenantId === null) {
    /*
      Signed in, and no establishment: a salon signing itself up, most of the
      time. Before, this screen said "ask for an invitation" and stopped there
      — right for a hairdresser joining a team, a dead end for the salon owner
      the whole sign-up exists for. The invitation path is still named on it.
    */
    return (
      <CreateEstablishment
        baseUrl={API_BASE_URL}
        token={token}
        onCreated={(created) => {
          // Asked again rather than patched locally: who this person is has
          // just changed, and the server is the one that says so. Until it
          // answers, the skeleton; if it does not, the failure and a way to
          // ask again — never this form a second time, because the
          // establishment now exists.
          setIdentity(null);
          setTenantId(created.tenantId);
          askIdentityAgain();
          navigate(CHOOSE_PLAN_PATH);
        }}
      />
    );
  }

  if (session === null) return <AppSkeleton />;
  const role = identity.memberships.find((m) => m.tenantId === tenantId)?.role ?? '';

  return (
    <Suspense fallback={<AppSkeleton />}>
      <Dashboard
        path={path}
        session={session}
        identity={identity}
        role={role}
        memberships={identity.memberships}
        tenantId={tenantId}
        onTenantChange={setTenantId}
        onSignOut={signOut}
      />
    </Suspense>
  );
}

/** A frame with nothing in it, for the moments before the server has answered. */
function Plain({ children }: { readonly children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="mx-auto max-w-2xl px-4 py-16">
      <div className="mb-8">
        <h1 className="text-subtitle font-bold text-ink">GSI Voice</h1>
      </div>
      {children}
    </div>
  );
}

function AppSkeleton(): React.JSX.Element {
  return (
    <div className="mx-auto max-w-5xl px-4 py-10">
      <Loading label="Chargement de votre espace">
        <div className="space-y-4">
          <Skeleton className="h-8 w-56" />
          <Skeleton className="h-32 w-full" rounded="card" />
        </div>
      </Loading>
    </div>
  );
}

/**
 * Turns a failure into something a salon owner can read.
 *
 * Branches on the server's stable code, never on its message. The API answers
 * in English because its messages are written for an operator and a log; this
 * dashboard is read by a French hairdresser, and handing them "Not permitted."
 * is how a product feels unfinished in the one moment somebody is already
 * having trouble.
 */
function messageFor(cause: unknown): string {
  if (cause instanceof ApiError) {
    switch (cause.code) {
      case 'authn.required':
        return 'Ce jeton n’est pas valide ou a expiré. Reconnectez-vous.';
      case 'authz.denied':
        return 'Ce compte n’a pas accès à cet établissement.';
      default:
        break;
    }
  }
  return describeFailure(cause);
}

/** Why a sign-in came back unfinished, in the language of the person reading. */
function signInMessageFor(code: string): string {
  switch (code) {
    case 'not_configured':
      return 'La connexion n’est pas encore configurée sur ce site.';
    case 'provider_error':
      return 'La connexion a été interrompue. Vous pouvez réessayer.';
    case 'provider_unavailable':
      return 'Le service d’identité ne répond pas. Réessayez dans quelques minutes.';
    case 'state_mismatch':
      return 'Cette tentative de connexion a expiré. Recommencez.';
    case 'exchange_failed':
      return 'Le fournisseur d’identité n’a pas répondu. Réessayez dans un instant.';
    case 'token_rejected':
      return 'La connexion a abouti mais le jeton reçu a été refusé : la configuration du fournisseur d’identité doit être vérifiée.';
    default:
      return 'La connexion a échoué.';
  }
}
